import { safeImageUri } from '@/lib/safe-image-uri';
import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { rawEnvelope } from '../http/envelopes';
import { ApiError } from '../http/errors';
export interface EditOwner {
  scope_kind: 'PROPERTY' | 'ROOM_TYPE' | 'ROOM' | 'UNIT';
  property_id: string;
  room_type_id: string | null;
  room_id: string | null;
  unit_id: string | null;
}
interface MediaDisplayFields {
  collection_id: string;
  item_id: string;
  category: string | null;
  language: 'ko' | 'en' | 'und';
  group_order: number;
  image_order: number;
}
export type MediaDisplay = MediaDisplayFields &
  (
    | { role: 'PROPERTY_CARD' | 'PROPERTY_HERO' | 'ROOM_TYPE_GALLERY' | 'UNIT_FLOOR_PLAN' }
    | { role: 'COMMON_AREA'; component_key: string }
  );
export interface EditSelection extends EditOwner {
  assignment_id: string;
  expected_assignment_revision: number;
  display_state: 'VISIBLE' | 'HIDDEN';
  displays: MediaDisplay[];
}
export interface MediaEditContext {
  expected_owner_version: number;
  collection_id: string;
  item_id: string;
  owner: EditOwner;
  expected_collection_revision: number;
  expected_item_revision: number;
  title: string | null;
  language: 'ko' | 'en' | 'und';
  codec_version: 2 | 3;
  primary_uri: string | null;
  preview_uri: string | null;
  availability: 'CURRENT' | 'ABSENT' | null;
  item_replace_eligibility:
    | 'SUPPORTED'
    | 'REFERENCE_KEY_MISMATCH'
    | 'MANAGED_REPLACEMENT_UNSUPPORTED';
  affected_selections: EditSelection[];
}
export interface SelectionCommand {
  command_id: string;
  expected_assignment_revision: number;
  display_state: 'VISIBLE' | 'HIDDEN';
  displays: MediaDisplay[];
}
export interface ItemCommand {
  expected_owner_version: number;
  command_id: string;
  expected_collection_revision: number;
  expected_item_revision: number;
  title: string | null;
  primary_uri: string;
  preview_uri: string | null;
  availability: 'CURRENT' | 'ABSENT';
  affected_selections: (SelectionCommand & { assignment_id: string })[];
}
const bad = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 사진 편집 문맥을 확인할 수 없습니다.',
  });
const rec = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const closed = (v: unknown, keys: string): v is Record<string, unknown> =>
  rec(v) && Object.keys(v).sort().join(',') === keys.split(' ').sort().join(',');
const positive = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
const order = (v: unknown) => Number.isSafeInteger(v) && Number(v) >= 0 && Number(v) <= 2147483647;
const nullableUuid = (v: unknown) => v === null || isUuid(v);
const language = (v: unknown) => ['ko', 'en', 'und'].includes(String(v));
const ownerKeys = 'scope_kind property_id room_type_id room_id unit_id';
function validOwner(v: Record<string, unknown>) {
  return (
    isUuid(v.property_id) &&
    nullableUuid(v.room_type_id) &&
    nullableUuid(v.room_id) &&
    nullableUuid(v.unit_id) &&
    (v.scope_kind === 'PROPERTY'
      ? v.room_type_id === null && v.room_id === null && v.unit_id === null
      : v.scope_kind === 'ROOM_TYPE'
        ? isUuid(v.room_type_id) && v.room_id === null && v.unit_id === null
        : v.scope_kind === 'UNIT'
          ? v.room_type_id === null && isUuid(v.room_id) && isUuid(v.unit_id)
          : v.scope_kind === 'ROOM' &&
            v.room_type_id === null &&
            isUuid(v.room_id) &&
            v.unit_id === null)
  );
}
function validDisplays(v: unknown): v is MediaDisplay[] {
  return (
    Array.isArray(v) &&
    v.length <= 4096 &&
    v.every(
      (d) =>
        rec(d) &&
        closed(
          d,
          `collection_id item_id role category language group_order image_order${d.role === 'COMMON_AREA' ? ' component_key' : ''}`,
        ) &&
        isUuid(d.collection_id) &&
        isUuid(d.item_id) &&
        (d.role === 'COMMON_AREA'
          ? typeof d.component_key === 'string' && d.component_key.length > 0
          : ['PROPERTY_CARD', 'PROPERTY_HERO', 'ROOM_TYPE_GALLERY', 'UNIT_FLOOR_PLAN'].includes(
              String(d.role),
            )) &&
        (d.category === null || typeof d.category === 'string') &&
        language(d.language) &&
        order(d.group_order) &&
        order(d.image_order),
    )
  );
}
export async function readMediaEditContext(
  client: ServiceClient,
  collection: string,
  item: string,
): Promise<MediaEditContext> {
  if (!isUuid(collection) || !isUuid(item)) throw bad();
  const v = await client.get<unknown>(
    `admin/media-collections/${collection}/items/${item}/edit-context`,
  );
  if (
    !closed(
      v,
      'expected_owner_version collection_id item_id owner expected_collection_revision expected_item_revision title language codec_version primary_uri preview_uri availability item_replace_eligibility affected_selections',
    ) ||
    !positive(v.expected_owner_version) ||
    v.collection_id !== collection ||
    v.item_id !== item ||
    !closed(v.owner, ownerKeys) ||
    !validOwner(v.owner) ||
    !positive(v.expected_collection_revision) ||
    !positive(v.expected_item_revision) ||
    !(v.title === null || typeof v.title === 'string') ||
    !language(v.language) ||
    !Array.isArray(v.affected_selections) ||
    v.affected_selections.length > 4096
  )
    throw bad();
  if (
    v.codec_version === 3
      ? v.primary_uri !== null ||
        v.preview_uri !== null ||
        v.availability !== null ||
        v.item_replace_eligibility !== 'MANAGED_REPLACEMENT_UNSUPPORTED'
      : v.codec_version !== 2 ||
        !(typeof v.primary_uri === 'string' && safeImageUri(v.primary_uri)) ||
        !(
          v.preview_uri === null ||
          (typeof v.preview_uri === 'string' && safeImageUri(v.preview_uri))
        ) ||
        !(v.availability === null || v.availability === 'CURRENT' || v.availability === 'ABSENT') ||
        !['SUPPORTED', 'REFERENCE_KEY_MISMATCH'].includes(String(v.item_replace_eligibility))
  )
    throw bad();
  const currentOwner = v.owner;
  const seen = new Set<string>();
  let total = 0;
  for (const s of v.affected_selections) {
    if (
      !closed(
        s,
        `${ownerKeys} assignment_id expected_assignment_revision display_state displays`,
      ) ||
      !validOwner(s) ||
      ownerKeys.split(' ').some((k) => s[k] !== currentOwner[k]) ||
      !isUuid(s.assignment_id) ||
      seen.has(s.assignment_id) ||
      !positive(s.expected_assignment_revision) ||
      !['VISIBLE', 'HIDDEN'].includes(String(s.display_state)) ||
      !validDisplays(s.displays) ||
      s.displays.some((d) => d.role === 'COMMON_AREA' && s.scope_kind !== 'PROPERTY') ||
      !s.displays.some((d) => d.collection_id === collection && d.item_id === item)
    )
      throw bad();
    seen.add(s.assignment_id);
    total += s.displays.length;
  }
  if (total > 4096) throw bad();
  return v as unknown as MediaEditContext;
}
export function selectionCommand(s: EditSelection): SelectionCommand {
  return {
    command_id: crypto.randomUUID(),
    expected_assignment_revision: s.expected_assignment_revision,
    display_state: s.display_state,
    displays: s.displays.map((d) => ({ ...d })),
  };
}
export function validateMediaReceipt(
  v: unknown,
  command: string,
  target: string,
  item: string | null,
) {
  if (
    !closed(
      v,
      'command_id target_id target_kind collection_id item_id result_revision collection_revision http_status',
    ) ||
    v.command_id !== command ||
    v.target_id !== target ||
    v.target_kind !== (item ? 'CONTENT_COLLECTION' : 'CONTENT_ASSIGNMENT') ||
    v.collection_id !== (item ? target : null) ||
    v.item_id !== item ||
    !positive(v.result_revision) ||
    (item ? !positive(v.collection_revision) : v.collection_revision !== null) ||
    v.http_status !== 200
  )
    throw bad();
}
export async function replaceMediaSelection(
  client: ServiceClient,
  assignment: string,
  body: SelectionCommand & { expected_owner_version: number },
) {
  if (
    !closed(
      body,
      'expected_owner_version command_id expected_assignment_revision display_state displays',
    ) ||
    !positive(body.expected_owner_version)
  )
    throw bad();
  const v = await client.put<unknown>(`admin/content-media-selections/${assignment}`, {
    json: body,
  });
  validateMediaReceipt(v, body.command_id, assignment, null);
}
export async function replaceMediaItem(
  client: ServiceClient,
  collection: string,
  item: string,
  body: ItemCommand,
) {
  if (
    !closed(
      body,
      'expected_owner_version command_id expected_collection_revision expected_item_revision title primary_uri preview_uri availability affected_selections',
    ) ||
    !positive(body.expected_owner_version) ||
    !Array.isArray(body.affected_selections) ||
    !body.affected_selections.every((selection) =>
      closed(
        selection,
        'command_id assignment_id expected_assignment_revision display_state displays',
      ),
    )
  )
    throw bad();
  const v = await client.put<unknown>(`admin/media-collections/${collection}/items/${item}`, {
    json: body,
    responseEnvelope: rawEnvelope,
  });
  validateMediaItemReceipt(v, collection, item, body);
}

export function validateMediaItemReceipt(
  v: unknown,
  collection: string,
  item: string,
  body: ItemCommand,
) {
  if (
    !closed(v, 'item selections') ||
    !Array.isArray(v.selections) ||
    v.selections.length !== body.affected_selections.length
  )
    throw bad();
  validateMediaReceipt(v.item, body.command_id, collection, item);
  v.selections.forEach((r, i) => {
    const s = body.affected_selections[i];
    if (!s) throw bad();
    validateMediaReceipt(r, s.command_id, s.assignment_id, null);
  });
}

/** New selection consumes the current owner row CAS; nested item selections keep child CAS only. */
export interface CreateMediaSelection extends Omit<EditOwner, 'scope_kind'> {
  scope_kind: 'PROPERTY' | 'ROOM_TYPE' | 'UNIT';
  expected_owner_version: number;
  command_id: string;
  display_state: 'VISIBLE' | 'HIDDEN';
  displays: MediaDisplay[];
}
export async function createMediaSelection(
  client: ServiceClient,
  body: CreateMediaSelection,
): Promise<string> {
  if (
    !closed(body, `${ownerKeys} expected_owner_version command_id display_state displays`) ||
    !positive(body.expected_owner_version) ||
    !['PROPERTY', 'ROOM_TYPE', 'UNIT'].includes(body.scope_kind) ||
    !validOwner(body) ||
    !isUuid(body.command_id) ||
    !['VISIBLE', 'HIDDEN'].includes(body.display_state) ||
    !validDisplays(body.displays) ||
    !body.displays.length ||
    body.displays.some(
      (d) =>
        !{
          PROPERTY: ['PROPERTY_CARD', 'PROPERTY_HERO', 'COMMON_AREA'],
          ROOM_TYPE: ['ROOM_TYPE_GALLERY'],
          UNIT: ['UNIT_FLOOR_PLAN'],
        }[body.scope_kind].includes(d.role),
    )
  )
    throw bad();
  const v = await client.post<unknown>('admin/content-media-selections', {
    json: body,
    expectedStatus: 201,
  });
  return decodeMediaSelectionCreated(v, body.command_id);
}

export function decodeMediaSelectionCreated(v: unknown, command: string): string {
  if (
    !closed(
      v,
      'command_id target_id target_kind collection_id item_id result_revision collection_revision http_status',
    ) ||
    v.command_id !== command ||
    !isUuid(v.target_id) ||
    v.target_kind !== 'CONTENT_ASSIGNMENT' ||
    v.collection_id !== null ||
    v.item_id !== null ||
    v.collection_revision !== null ||
    !positive(v.result_revision) ||
    v.http_status !== 201
  )
    throw bad();
  return v.target_id;
}

export interface OwnerMediaSelectionContext {
  expected_owner_version: number;
  owner: EditOwner;
  selection_state: 'ABSENT' | 'LIVE' | 'RESERVED';
  current_selection: EditSelection | null;
  items: {
    collection_id: string;
    item_id: string;
    title: string | null;
    language: 'ko' | 'en' | 'und';
    codec_version: 2 | 3;
    primary_uri: string | null;
    preview_uri: string | null;
    availability: 'CURRENT' | 'ABSENT' | null;
  }[];
}
/** Local backend packet core-media-owner-context-implementation-20261030; serving unverified. */
export async function readOwnerMediaSelectionContext(
  client: ServiceClient,
  expected: EditOwner,
): Promise<OwnerMediaSelectionContext> {
  if (!closed(expected, ownerKeys) || !validOwner(expected) || expected.scope_kind === 'ROOM')
    throw bad();
  const v = await client.get<unknown>(
    `admin/properties/${expected.property_id}/media-selection-context`,
    {
      params: {
        scope_kind: expected.scope_kind,
        ...(expected.scope_kind === 'ROOM_TYPE' ? { room_type_id: expected.room_type_id } : {}),
        ...(expected.scope_kind === 'UNIT'
          ? { room_id: expected.room_id, unit_id: expected.unit_id }
          : {}),
      },
    },
  );
  if (
    !closed(v, 'expected_owner_version owner selection_state current_selection items') ||
    !positive(v.expected_owner_version) ||
    !closed(v.owner, ownerKeys) ||
    !validOwner(v.owner)
  )
    throw bad();
  const actualOwner = v.owner;
  if (
    ownerKeys.split(' ').some((key) => actualOwner[key] !== expected[key as keyof EditOwner]) ||
    !['ABSENT', 'LIVE', 'RESERVED'].includes(String(v.selection_state)) ||
    !Array.isArray(v.items) ||
    v.items.length > 4096
  )
    throw bad();
  if (v.selection_state !== 'LIVE') {
    if (v.current_selection !== null || v.items.length !== 0) throw bad();
    return v as unknown as OwnerMediaSelectionContext;
  }
  const s = v.current_selection;
  if (
    !closed(s, `${ownerKeys} assignment_id expected_assignment_revision display_state displays`) ||
    !validOwner(s) ||
    ownerKeys.split(' ').some((key) => s[key] !== expected[key as keyof EditOwner]) ||
    !isUuid(s.assignment_id) ||
    !positive(s.expected_assignment_revision) ||
    !['VISIBLE', 'HIDDEN'].includes(String(s.display_state)) ||
    !validDisplays(s.displays)
  )
    throw bad();
  const refs = new Set<string>();
  for (const item of v.items) {
    if (
      !closed(
        item,
        'collection_id item_id title language codec_version primary_uri preview_uri availability',
      ) ||
      !isUuid(item.collection_id) ||
      !isUuid(item.item_id) ||
      !(item.title === null || typeof item.title === 'string') ||
      !language(item.language)
    )
      throw bad();
    if (
      item.codec_version === 3
        ? item.primary_uri !== null || item.preview_uri !== null || item.availability !== null
        : item.codec_version !== 2 ||
          typeof item.primary_uri !== 'string' ||
          !safeImageUri(item.primary_uri) ||
          !(
            item.preview_uri === null ||
            (typeof item.preview_uri === 'string' && safeImageUri(item.preview_uri))
          ) ||
          !(
            item.availability === null ||
            item.availability === 'CURRENT' ||
            item.availability === 'ABSENT'
          )
    )
      throw bad();
    const key = `${item.collection_id}:${item.item_id}`;
    if (
      refs.has(key) ||
      !s.displays.some(
        (d) => `${d.collection_id}:${d.item_id}` === key && d.language === item.language,
      )
    )
      throw bad();
    refs.add(key);
  }
  const roles =
    expected.scope_kind === 'PROPERTY'
      ? ['PROPERTY_CARD', 'PROPERTY_HERO', 'COMMON_AREA']
      : expected.scope_kind === 'ROOM_TYPE'
        ? ['ROOM_TYPE_GALLERY']
        : ['UNIT_FLOOR_PLAN'];
  if (
    s.displays.some((d) => !refs.has(`${d.collection_id}:${d.item_id}`) || !roles.includes(d.role))
  )
    throw bad();
  return v as unknown as OwnerMediaSelectionContext;
}

export interface MediaRetirementCommand {
  command_id: string;
  expected_collection_revision: number;
  expected_item_revision?: number;
}
export async function retireMedia(
  client: ServiceClient,
  collection: string,
  item: string | null,
  body: MediaRetirementCommand,
) {
  if (
    !isUuid(collection) ||
    (item !== null && !isUuid(item)) ||
    !closed(
      body,
      `command_id expected_collection_revision${item ? ' expected_item_revision' : ''}`,
    ) ||
    !isUuid(body.command_id) ||
    !positive(body.expected_collection_revision) ||
    (item !== null && !positive(body.expected_item_revision))
  )
    throw bad();
  const v = await client.post<unknown>(
    `admin/media-collections/${collection}${item ? `/items/${item}` : ''}/retirements`,
    { json: body },
  );
  if (
    !closed(
      v,
      'command_id target_id target_kind collection_id item_id result_revision collection_revision http_status',
    ) ||
    v.command_id !== body.command_id ||
    v.target_id !== collection ||
    v.target_kind !== 'CONTENT_COLLECTION' ||
    v.collection_id !== collection ||
    v.item_id !== item ||
    v.http_status !== 200 ||
    v.result_revision !== body.expected_collection_revision + 1 ||
    v.collection_revision !== v.result_revision
  )
    throw bad();
}
